import * as logger from 'loglevel'
import {
  parseDurationAttribute,
  parsePromptAttribute,
  parseResetAttribute,
  parseExitAttribute,
  parseExitActionAttribute,
  hasPromptAttribute,
} from '../Utils/ActionTimeout'
import {
  DEFAULT_ACTION_TIMEOUT_RESET_LABEL,
  DEFAULT_ACTION_TIMEOUT_EXIT_LABEL,
} from '../Utils/ActionTimeout'

/**
 * Web component that wraps one or more <anear-action> and/or <anear-form> elements.
 *
 * Usage (with prompt modal):
 *   <anear-timeout duration="20000,15000" prompt="..." reset="Yes" exit="No">
 *     <anear-action payload='{"FOO":null}'>Foo</anear-action>
 *   </anear-timeout>
 *
 * Usage (no prompt – single duration, then ACTION_TIMEOUT):
 *   <anear-timeout duration="15000">
 *     <anear-action payload='{"READY":{}}'>Continue</anear-action>
 *   </anear-timeout>
 *
 * Behavior:
 * - Arms a local timer when connected (uses first value of duration in ms).
 * - When the timer expires, disables descendant actions/forms.
 * - If prompt is not present (missing or empty): emits anear-timeout-expired with
 *   sendActionTimeout: true; the ABR sends ACTION_TIMEOUT on the actions channel (no modal).
 * - If prompt is present: emits anear-timeout-expired with prompt/reset/exit so the ABR
 *   shows a modal; disarmForUserAction() before publishing any action/form;
 *   resetFromYes() when the user confirms they still want to participate.
 *
 * Works in both public (full-frame) and private (anchor-injected) content.
 */
class AnearTimeout extends HTMLElement {
  constructor() {
    super()
    this._timerId = null
    this._armSeq = 0
    this._armed = false
    this._expired = false
    this._promptDispatched = false
    this._promptDismissed = false
    this._lastParsed = null
  }

  static get observedAttributes() {
    return ['duration', 'prompt', 'reset', 'reset-action', 'exit', 'exit-action', 'started-at', 'remaining-ms']
  }

  connectedCallback() {
    // Reset-on-rerender semantics: a fresh element always arms from scratch.
    this._armFromAttributes()
  }

  disconnectedCallback() {
    this._clearTimer()
  }

  attributeChangedCallback(name, _oldValue, _newValue) {
    // If the markup updates attributes in-place, re-arm with new values.
    if (
      name === 'duration' ||
      name === 'prompt' ||
      name === 'reset' ||
      name === 'reset-action' ||
      name === 'exit' ||
      name === 'exit-action' ||
      name === 'started-at' ||
      name === 'remaining-ms'
    ) {
      this._armFromAttributes()
    }
  }

  _clearTimer() {
    if (this._timerId) {
      clearTimeout(this._timerId)
      this._timerId = null
    }
  }

  _resolveActionDelayMs(actionMs, ignoreResume = false) {
    if (ignoreResume) return actionMs

    const remainingMsAttr = Number.parseInt(this.getAttribute('remaining-ms'), 10)
    if (Number.isFinite(remainingMsAttr) && remainingMsAttr >= 0) {
      return Math.max(0, Math.min(actionMs, remainingMsAttr))
    }

    const startedAtAttr = Number.parseInt(this.getAttribute('started-at'), 10)
    if (Number.isFinite(startedAtAttr) && startedAtAttr > 0) {
      const elapsedMs = Date.now() - startedAtAttr
      return Math.max(0, actionMs - elapsedMs)
    }

    return actionMs
  }

  _armFromAttributes({ ignoreResume = false } = {}) {
    this._clearTimer()
    this._armed = false
    this._expired = false
    this._promptDispatched = false
    this._promptDismissed = false
    this.removeAttribute('data-anear-timeout-expired')

    const parsed = parseDurationAttribute(this.getAttribute('duration'))
    this._lastParsed = parsed
    if (!parsed) return

    // If this wrapper contains no actionable descendants, treat it as a semantic/UX anchor only.
    // This avoids arming timers in spectator-only views (or future timer-bar anchors) where there
    // are no interactive actions/forms to gate.
    const actionableCount = this._descendantsToGate().length
    if (actionableCount <= 0) {
      logger.debug('[anear-timeout] skipping arm: no <anear-action>/<anear-form> descendants', {
        id: (this.getAttribute('id') || '').trim() || null,
        duration: this.getAttribute('duration'),
      })
      return
    }

    this._armed = true
    this._enableChildren()
    const actionDelayMs = this._resolveActionDelayMs(parsed.actionMs, ignoreResume)
    const armSeq = ++this._armSeq

    // Intentionally no log on arm: this can be noisy during normal renders.
    this._timerId = setTimeout(() => {
      if (armSeq !== this._armSeq) return
      this._expire()
    }, actionDelayMs)
  }

  _expire() {
    if (this._expired || !this._armed) return
    this._expired = true
    this._armed = false
    this.setAttribute('data-anear-timeout-expired', 'true')
    this._disableChildren()
    this.requestPrompt()
  }

  _descendantsToGate() {
    // Gate Anear action/form/nav so the timeout arms when wrapping e.g. EXIT (anear-nav).
    return this.querySelectorAll('anear-action, anear-form, anear-nav')
  }

  _disableChildren() {
    this._descendantsToGate().forEach(el => {
      try {
        el.setAttribute('aria-disabled', 'true')
        el.setAttribute('data-anear-timeout-disabled', 'true')
      } catch (_e) {
        // ignore
      }
    })
  }

  _enableChildren() {
    this._descendantsToGate().forEach(el => {
      try {
        el.removeAttribute('aria-disabled')
        el.removeAttribute('data-anear-timeout-disabled')
      } catch (_e) {
        // ignore
      }
    })
  }

  // Called by ABR before publishing a user action/form submit.
  disarmForUserAction() {
    if (!this._armed && !this._promptDismissed) return
    this._armed = false
    this._promptDismissed = false
    this._armSeq += 1
    this._clearTimer()
  }

  // Called by ABR when the user answers YES on the timeout prompt.
  resetFromYes() {
    this._armFromAttributes({ ignoreResume: true })
  }

  // Close the prompt and re-enable controls without re-arming phase 1.
  // Modal penalty timer continues in the ABR until bid/action or expiry.
  dismissPrompt() {
    this._promptDismissed = true
    this._enableChildren()
  }

  isActionExpired() {
    if (this._promptDismissed) return false
    return this._expired || this.getAttribute('data-anear-timeout-expired') === 'true'
  }

  requestPrompt() {
    if (this._promptDispatched) return
    const parsed = this._lastParsed || parseDurationAttribute(this.getAttribute('duration'))
    if (!parsed) return

    this._promptDispatched = true
    const timeoutId = (this.getAttribute('id') || '').trim() || null

    // When prompt is not present (missing or empty): no modal; tell host to send ACTION_TIMEOUT on actions channel.
    if (!hasPromptAttribute(this)) {
      this.dispatchEvent(new CustomEvent('anear-timeout-expired', {
        bubbles: true,
        composed: true,
        detail: {
          timeoutEl: this,
          timeoutId,
          sendActionTimeout: true,
          actionMs: parsed.actionMs,
          source: 'group',
        },
      }))
      return
    }

    const prompt = parsePromptAttribute(this.getAttribute('prompt'))

    // Defaults:
    // - If neither reset nor exit are specified, show both (Yes/No).
    // - If reset is specified but exit is omitted, show reset only (per your examples).
    const hasReset = this.hasAttribute('reset')
    const hasExit = this.hasAttribute('exit')

    const resetLabel = hasReset ? parseResetAttribute(this.getAttribute('reset')) : DEFAULT_ACTION_TIMEOUT_RESET_LABEL
    let exitLabel = null
    if (hasExit) {
      exitLabel = parseExitAttribute(this.getAttribute('exit')) || DEFAULT_ACTION_TIMEOUT_EXIT_LABEL
    } else if (!hasReset) {
      exitLabel = DEFAULT_ACTION_TIMEOUT_EXIT_LABEL
    }

    const exitAction = parseExitActionAttribute(this.getAttribute('exit-action'))

    // Bubble so EventFrame/ChildEventFrame can listen on the stable container.
    this.dispatchEvent(new CustomEvent('anear-timeout-expired', {
      bubbles: true,
      composed: true,
      detail: {
        timeoutEl: this,
        timeoutId,
        prompt,
        resetLabel,
        exitLabel,
        exitAction,
        actionMs: parsed.actionMs,
        modalMs: parsed.modalMs,
        source: 'group',
      },
    }))
  }
}

if (!customElements.get('anear-timeout')) {
  customElements.define('anear-timeout', AnearTimeout)
}

export default AnearTimeout

